import React, { useMemo } from 'react'
import { createColumnHelper } from '@tanstack/react-table'
import { Box } from '@mui/material'

import { useGetB2BQrs } from '@/services'
import { B2BQr } from '@/types.ts'
import { TableComponent } from '@/components/table'
import { FetchErrorMessage } from '@/components'
import { DefaultFiltersState } from '@/stores'
import { useIsMobile } from '@/hooks'
import { useAppDispatch, useAppSelector } from '@/stores/hooks.ts'
import { b2bActions, b2bSelectors } from '@/stores/slices/b2b-slice.ts'
import { userSelectors } from '@/stores/slices/user-slice.ts'

import { defaultSettings, KEY_PAGE, PartialB2BQr, getColumnsSettings } from '../default-settings-b2b-page.tsx'

const columnHelper = createColumnHelper<B2BQr>()

const defaultColumns = getColumnsSettings(columnHelper)
const defaultSettingsKey = Object.keys(defaultSettings)

export const B2BQrsTable = (): React.ReactElement => {
  const { data, isLoading, isFetching, isError, refetch } = useGetB2BQrs()
  const { page, showCount } = useAppSelector(b2bSelectors.filters)
  const dispatch = useAppDispatch()
  const settings = useAppSelector((state) => userSelectors.personalSettings(state, KEY_PAGE))
  const isMobile = useIsMobile()

  const handlePaginationParam = (param: keyof DefaultFiltersState) => (value: number) =>
    dispatch(
      b2bActions.setFilter({
        filterKey: param,
        filterValue: value,
      })
    )

  const qrsB2BCols = useMemo(() => {
    const format = isMobile ? 'mobile' : 'desktop'

    const desktopSettings = (settings?.[format] ?? defaultSettingsKey) as Array<keyof PartialB2BQr>

    const dynamicSettings = desktopSettings.filter((e) => defaultSettingsKey.includes(e)).map((e) => defaultColumns[e])
    return [...dynamicSettings]
  }, [settings, isMobile])

  return (
    <FetchErrorMessage refetch={refetch} isFetching={isFetching} isError={isError}>
      <Box mt={3}>
        <TableComponent<B2BQr>
          rowKey="requestId"
          columns={qrsB2BCols}
          page={page}
          isLoading={isLoading || isFetching}
          size={showCount}
          data={data?.data?.content ?? []}
          totalPages={data?.data?.totalPages}
          onPageChange={handlePaginationParam('page')}
          onSizeChange={handlePaginationParam('showCount')}
        />
      </Box>
    </FetchErrorMessage>
  )
}
